/* eslint-disable jsx-a11y/no-static-element-interactions */

/* eslint-disable react/jsx-props-no-spreading */
import React, {
  CSSProperties,
  ComponentPropsWithoutRef,
  ReactNode,
  useState
} from 'react';

import Tokens from 'styles/tokens';

import * as Popover from '@radix-ui/react-popover';
import AvomaTooltipV2 from './AvomaTooltipV2';

interface IAvomaPopoverControlledProps {
  align?: ComponentPropsWithoutRef<typeof Popover.Content>['align'];
  position?: ComponentPropsWithoutRef<typeof Popover.Content>['side'];
  dropdownTrigger?: () => ReactNode;
  wrapperStyle?: CSSProperties;
  triggerStyle?: CSSProperties;
  children?: ReactNode;
  contentProps?: ComponentPropsWithoutRef<typeof Popover.Content>;
  triggerProps?: ComponentPropsWithoutRef<typeof Popover.Trigger>;
  controlled?: boolean;
  open?: boolean;
  setOpen?: (open: boolean) => void;
  closeCleanupHandler?: () => void;
  triggerContainerProps?: React.HTMLAttributes<HTMLDivElement>;
  contentClasses?: string;
  triggerClasses?: string;
  portalContainer?: ComponentPropsWithoutRef<
    typeof Popover.Portal
  >['container'];
  triggerTooltipProps?: Omit<
    ComponentPropsWithoutRef<typeof AvomaTooltipV2>,
    'children'
  >;
  sideOffset?: number;
  onOpenChange?: (open: boolean) => void;
  modal?: boolean;
  disableOutsideClick?: boolean;
  onPointerDownOutside?: ComponentPropsWithoutRef<
    typeof Popover.Content
  >['onPointerDownOutside'];
  // Preserve compatibility with existing JS/TS call sites that pass
  // legacy unused props to this wrapper component.
  [key: string]: unknown;
}

const AvomaPopoverControlled = ({
  align = 'start',
  position = 'bottom',
  dropdownTrigger,
  wrapperStyle,
  triggerStyle,
  children,
  contentProps,
  triggerProps,
  controlled,
  open,
  setOpen,
  closeCleanupHandler,
  triggerContainerProps,
  contentClasses,
  triggerClasses,
  portalContainer = document.body,
  triggerTooltipProps,
  sideOffset = 6,
  onOpenChange,
  modal = false,
  disableOutsideClick = false,
  onPointerDownOutside: onPointerDownOutsideProp
}: IAvomaPopoverControlledProps) => {
  const [internalPopoverState, setInternalPopoverState] = useState(false);
  const popoverState = controlled ? open : internalPopoverState;
  const setPopoverState = controlled ? setOpen! : setInternalPopoverState;

  const handlePointerDownOutside: NonNullable<
    ComponentPropsWithoutRef<typeof Popover.Content>['onPointerDownOutside']
  > = e => {
    if (onPointerDownOutsideProp) {
      onPointerDownOutsideProp(e);
    }
    if (e.defaultPrevented) {
      return;
    }
    if (disableOutsideClick) {
      e.preventDefault();
    }
  };

  return (
    <Popover.Root
      modal={modal}
      open={popoverState}
      onOpenChange={newOpen => {
        if (popoverState && typeof closeCleanupHandler === 'function') {
          closeCleanupHandler();
        }
        if (onOpenChange) {
          onOpenChange(newOpen);
        }
        // Radix passes the next open state; toggling here desyncs controlled popovers and breaks in-content clicks (e.g. checkboxes).
        setPopoverState(newOpen);
      }}
    >
      {dropdownTrigger && (
        <AvomaTooltipV2 {...triggerTooltipProps}>
          <Popover.Trigger
            asChild
            style={{ ...triggerStyle }}
            {...triggerProps}
          >
            <div className={triggerClasses} {...triggerContainerProps}>
              {dropdownTrigger()}
            </div>
          </Popover.Trigger>
        </AvomaTooltipV2>
      )}
      {children && (
        <Popover.Portal container={portalContainer}>
          <Popover.Content
            onPointerDownOutside={handlePointerDownOutside}
            align={align}
            side={position}
            sideOffset={sideOffset}
            collisionPadding={8}
            style={{
              ...(children && styles.dropdownWrapper),
              ...wrapperStyle
            }}
            className={contentClasses}
            {...contentProps}
          >
            {children}
          </Popover.Content>
        </Popover.Portal>
      )}
    </Popover.Root>
  );
};

const styles: Record<'dropdownWrapper', CSSProperties> = {
  dropdownWrapper: {
    ...Tokens.shadows.medium,
    backgroundColor: Tokens.colors.white,
    border: `1px solid ${Tokens.colors.gainsboro}`,
    borderRadius: Tokens.spacing.borderRadius,
    padding: Tokens.spacing.two,
    zIndex: 999999
  }
};

export default AvomaPopoverControlled;
